import { z } from 'zod';
import { PlanCode } from './plan';

export const AI_MODEL_ID_MAX_LENGTH = 128;

export function buildAiModelId(code: string): string | null {
  const trimmedCode = code.trim();
  if (!/^[a-zA-Z0-9._:/-]+$/.test(trimmedCode)) return null;

  const normalizedCode = trimmedCode
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!normalizedCode) return null;

  const id = `mdl_${normalizedCode}`;
  return id.length <= AI_MODEL_ID_MAX_LENGTH ? id : null;
}

export const AdminSubscriptionBillingCycle = z.enum(['monthly', 'yearly']);
export type AdminSubscriptionBillingCycle = z.infer<typeof AdminSubscriptionBillingCycle>;

export const AdminSubscriptionActivationOperation = z.enum([
  'activate',
  'renew',
  'replace',
  'set_free',
]);
export type AdminSubscriptionActivationOperation = z.infer<
  typeof AdminSubscriptionActivationOperation
>;

export const AdminSubscriptionPaymentSource = z.enum(['admin_grant', 'admin_offline_payment']);
export type AdminSubscriptionPaymentSource = z.infer<typeof AdminSubscriptionPaymentSource>;

const adminSubscriptionActivationInput = z
  .object({
    plan_code: PlanCode,
    billing_cycle: AdminSubscriptionBillingCycle.nullable(),
    amount_cents: z.number().int().min(0).max(100_000_000),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

function validateAdminSubscriptionActivationInput(
  value: z.infer<typeof adminSubscriptionActivationInput>,
  context: z.RefinementCtx,
): void {
  if (value.plan_code === 'free') {
    if (value.billing_cycle !== null) {
      context.addIssue({
        code: 'custom',
        path: ['billing_cycle'],
        message: 'Free plan must not have a billing cycle',
      });
    }
    if (value.amount_cents !== 0) {
      context.addIssue({
        code: 'custom',
        path: ['amount_cents'],
        message: 'Free plan amount must be zero',
      });
    }
    return;
  }

  if (value.billing_cycle === null) {
    context.addIssue({
      code: 'custom',
      path: ['billing_cycle'],
      message: 'Paid plan requires a billing cycle',
    });
  }
}

export const AdminSubscriptionActivationPreviewRequest =
  adminSubscriptionActivationInput.superRefine(validateAdminSubscriptionActivationInput);
export type AdminSubscriptionActivationPreviewRequest = z.infer<
  typeof AdminSubscriptionActivationPreviewRequest
>;

export const AdminSubscriptionActivationCreateRequest = adminSubscriptionActivationInput
  .extend({
    state_token: z.string().min(16).max(128),
    idempotency_key: z.string().uuid(),
  })
  .superRefine(validateAdminSubscriptionActivationInput);
export type AdminSubscriptionActivationCreateRequest = z.infer<
  typeof AdminSubscriptionActivationCreateRequest
>;

const AdminSubscriptionActivationTenant = z.object({
  id: z.string(),
  name: z.string(),
  owner_email: z.string().email(),
});

const AdminSubscriptionActivationPlan = z.object({
  code: PlanCode,
  display_name: z.string(),
});

const AdminSubscriptionActivationCurrentSubscription = z.object({
  id: z.string(),
  plan_code: PlanCode,
  billing_cycle: AdminSubscriptionBillingCycle,
  status: z.enum(['trialing', 'active', 'past_due', 'paused']),
  current_period_start: z.string(),
  current_period_end: z.string(),
});

export const AdminSubscriptionActivationPreviewResponse = z.object({
  state_token: z.string().min(16).max(128),
  tenant: AdminSubscriptionActivationTenant,
  current_plan: AdminSubscriptionActivationPlan,
  current_subscription: AdminSubscriptionActivationCurrentSubscription.nullable(),
  target_plan: AdminSubscriptionActivationPlan.extend({
    currency: z.string(),
    standard_amount_cents: z.number().int().nonnegative(),
  }),
  operation: AdminSubscriptionActivationOperation,
  billing_cycle: AdminSubscriptionBillingCycle.nullable(),
  amount_cents: z.number().int().nonnegative(),
  payment_source: AdminSubscriptionPaymentSource,
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
});
export type AdminSubscriptionActivationPreviewResponse = z.infer<
  typeof AdminSubscriptionActivationPreviewResponse
>;

export const AdminSubscriptionActivationResult = z.object({
  id: z.string(),
  operation: AdminSubscriptionActivationOperation,
  tenant_id: z.string(),
  previous_plan_code: PlanCode,
  target_plan_code: PlanCode,
  billing_cycle: AdminSubscriptionBillingCycle.nullable(),
  amount_cents: z.number().int().nonnegative(),
  currency: z.string(),
  payment_source: AdminSubscriptionPaymentSource,
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  subscription_id: z.string().nullable(),
  invoice_id: z.string(),
  payment_id: z.string(),
  created_at: z.string(),
  replayed: z.boolean(),
});
export type AdminSubscriptionActivationResult = z.infer<typeof AdminSubscriptionActivationResult>;

export const AdminSubscriptionActivationCreateResponse = z.object({
  activation: AdminSubscriptionActivationResult,
});
export type AdminSubscriptionActivationCreateResponse = z.infer<
  typeof AdminSubscriptionActivationCreateResponse
>;
